import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

function formatDateLabel(ymd) {
  if (!ymd) return "";
  const [y, m, d] = ymd.split("-").map(Number);
  if (!y || !m || !d) return ymd;
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const days = ["Sun", "Mon", "Tue", "Wed", "Thurs", "Fri", "Sat"];
  const date = new Date(y, m - 1, d);
  return `${days[date.getDay()]} ${months[m - 1]} ${d}`;
}

function renderActivityText(label) {
  const text = (label || "").toString();
  if (!text.includes("/")) return text;
  const parts = text.split("/");
  return parts.map((part, idx) => (
    <React.Fragment key={`${part}-${idx}`}>
      {idx > 0 ? "/\u200b" : ""}
      {part.trimStart()}
    </React.Fragment>
  ));
}

function DayRow({ label, value, urls, startTime }) {
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
      <div style={{ fontWeight: 700, minWidth: 60, whiteSpace: "nowrap" }}>{startTime || label}</div>
      <div className="activity-text">
        {renderActivityText(value)}
        {urls?.length ? (
          <span style={{ marginLeft: 8 }}>
            <a href={urls[0]} target="_blank" rel="noreferrer">
              View activity
            </a>
          </span>
        ) : null}
      </div>
    </div>
  );
}

function GuestCard({ guest, selectedDate }) {
  const isHorseback = (label) => (label || "").toLowerCase().includes("horseback riding");
  const formatActivity = (label) =>
    isHorseback(label) && guest.level ? `${label} - ${guest.level}` : label;
  const visibleDays = selectedDate
    ? (guest.days || []).filter((day) => day.date === selectedDate)
    : (guest.days || []);
  return (
    <div
      className="family-guest-card"
      style={{
        border: "1px solid #c6ced0",
        borderRadius: 12,
        padding: 12,
        background: "rgba(255, 255, 255, 0.92)",
        boxShadow: "0 10px 24px rgba(32, 65, 81, 0.12)",
        display: "grid",
        gap: 10,
      }}
    >
      <div style={{ fontWeight: 800 }}>{guest.name || "Guest"}</div>
      <div style={{ fontSize: 12, opacity: 0.7 }}>
        {guest.arrivalDate ? `Arrive ${guest.arrivalDate}` : ""}
        {guest.arrivalDate && guest.departureDate ? " • " : ""}
        {guest.departureDate ? `Depart ${guest.departureDate}` : ""}
      </div>
      {visibleDays.length ? (
        <div style={{ display: "grid", gap: 8 }}>
          {visibleDays.map((day) => (
            <div
              key={day.date}
              style={{
                border: "1px solid #d5dcdd",
                borderRadius: 10,
                padding: 10,
                background: "rgba(255, 255, 255, 0.88)",
                display: "grid",
                gap: 6,
              }}
            >
              <div style={{ fontWeight: 700 }}>{formatDateLabel(day.date)}</div>
              <DayRow
                label="AM"
                value={day.am === null ? "Arrival and Lunch" : day.am ? formatActivity(day.am.activity) : "—"}
                urls={day.am === null ? undefined : day.am?.activityUrls}
                startTime={day.am === null ? "11:45 AM" : day.am?.startTime}
              />
              <DayRow
                label="PM"
                value={day.pm ? formatActivity(day.pm.activity) : "—"}
                urls={day.pm?.activityUrls}
                startTime={day.pm?.startTime}
              />
            </div>
          ))}
        </div>
      ) : (
        <div style={{ opacity: 0.7 }}>
          {selectedDate ? "No activities for selected day." : "No scheduled days available."}
        </div>
      )}
    </div>
  );
}

export default function FamilyView() {
  const { familyId } = useParams();
  const [family, setFamily] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedDate, setSelectedDate] = useState("");

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError("");
      try {
        const res = await fetch(`/api/families/${familyId}/itinerary`);
        if (!res.ok) throw new Error("Unable to load itinerary.");
        const json = await res.json();
        if (!cancelled) setFamily(json);
      } catch (e) {
        if (!cancelled) setError(e.message || "Unable to load itinerary.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    if (familyId) load();
    return () => {
      cancelled = true;
    };
  }, [familyId]);

  return (
    <div style={{ maxWidth: 760, margin: "0 auto", padding: 16 }}>
      <style>{`
        .family-guest-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }
        .family-guest-card {
          break-inside: avoid;
          page-break-inside: avoid;
        }
        @media (max-width: 640px) {
          .family-guest-grid {
            grid-template-columns: 1fr;
          }
        }
        @media print {
          .family-guest-grid {
            grid-template-columns: 1fr 1fr;
          }
          .family-guest-card a {
            display: none;
          }
        }
      `}</style>
      {loading && <div>Loading itinerary…</div>}
      {error && <div style={{ color: "crimson" }}>{error}</div>}

      {!loading && !error && family && (
        <div style={{ display: "grid", gap: 16 }}>
          <div style={{ border: "1px solid #ddd", borderRadius: 12, padding: 16 }}>
            <div style={{ fontSize: 20, fontWeight: 800 }}>{family.name || "Family"}</div>
            {family.itineraryMessage ? (
              <div style={{ marginTop: 10, whiteSpace: "pre-wrap" }}>{family.itineraryMessage}</div>
            ) : null}
            {family.packingList?.length ? (
              <div style={{ marginTop: 10 }}>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>Packing List</div>
                <div style={{ display: "grid", gap: 4 }}>
                  {family.packingList.map((att) => (
                    <a key={att.id} href={att.url} target="_blank" rel="noreferrer" style={{ fontSize: 14 }}>
                      {att.filename}
                    </a>
                  ))}
                </div>
              </div>
            ) : null}
            <div style={{ marginTop: 6, fontSize: 12, opacity: 0.7 }}>View-only itinerary</div>
            <div style={{ marginTop: 12, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <label style={{ fontSize: 12, fontWeight: 700 }}>Filter by day</label>
              <select
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                style={{ minWidth: 180 }}
              >
                <option value="">All days</option>
                {Array.from(
                  new Set(
                    (family.guests || [])
                      .flatMap((g) => g.days || [])
                      .map((d) => d.date)
                      .filter(Boolean)
                  )
                )
                  .sort()
                  .map((day) => (
                    <option key={day} value={day}>
                      {formatDateLabel(day)}
                    </option>
                  ))}
              </select>
            </div>
          </div>

          {family.guests?.length ? (
            <div className="family-guest-grid">
              {[...(family.guests || [])]
                .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                .map((g) => (
                <GuestCard key={g.guestId} guest={g} selectedDate={selectedDate} />
              ))}
            </div>
          ) : (
            <div style={{ opacity: 0.7 }}>No linked guests found.</div>
          )}
        </div>
      )}
    </div>
  );
}
